<script setup>
// ============================================================================
// BonBoZ: mục "Trang được xem" trong hộp sửa nhân viên — issue #147.
//
// Trước đây muốn cho một người xem đủ 10 trang phải vào Cài đặt → Hộp thư →
// từng trang → Cộng tác viên, 10 lượt. Ở đây tick một lần, lưu cùng nút Lưu
// của hộp sửa (EditAgent.vue gọi `luu()` qua ref).
//
// Lưu qua PATCH /bonboz/nhan_vien/:id/trang — server tính thêm/bỏ trong một
// transaction và chỉ động tới dòng của người này. KHÔNG dùng PATCH
// /inbox_members của upstream: lời gọi đó thay nguyên danh sách người của một
// trang, gửi bản cũ trong bộ nhớ là xoá mất người admin khác vừa gán.
//
// Quản trị viên luôn thấy mọi trang (`User#assigned_inboxes`) bất kể được gán
// hay không, nên với admin chỉ hiện một dòng giải thích, không hiện ô tick.
// ============================================================================
import { computed, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import NhanVienAPI from 'dashboard/api/bonboz/nhanVien';
import { loiApi } from 'dashboard/api/pageConfigs';
import { demThayDoi } from 'dashboard/helper/bonboz/boChon';
import {
  bangTrang,
  napBangTrang,
  trangCua,
  datTrangCua,
} from 'dashboard/helper/bonboz/trangNhanVien';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ChonMuc from 'dashboard/components/ui/SelectMenu.vue';

const props = defineProps({
  agentId: { type: Number, required: true },
  // Theo vai trò ĐANG CHỌN trong hộp sửa, không phải vai trò đã lưu — đổi
  // sang quản trị viên là ô tick biến mất ngay.
  laAdmin: { type: Boolean, default: false },
});

const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');
const agents = useMapGetter('agents/getAgents');

const goc = ref(new Set());
const chon = ref(new Set());
const dangTai = ref(true);
const loiTai = ref('');
const nguoiMau = ref('');

if (!inboxes.value.length) store.dispatch('inboxes/get');
if (!bangTrang.value) napBangTrang();

// Đọc bản MỚI NHẤT của đúng người này lúc mở hộp, không lấy từ bảng đã nạp
// lúc vào màn — admin khác có thể vừa đổi.
NhanVienAPI.trang(props.agentId)
  .then(({ data }) => {
    goc.value = new Set(data.inbox_ids.map(Number));
    chon.value = new Set(goc.value);
  })
  .catch(e => {
    loiTai.value = loiApi(e);
  })
  .finally(() => {
    dangTai.value = false;
  });

const thayDoi = computed(() => demThayDoi(goc.value, chon.value));

const doi = id => {
  const next = new Set(chon.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  chon.value = next;
};
const datHet = bat => {
  chon.value = bat ? new Set(inboxes.value.map(i => i.id)) : new Set();
};

// "Giống như <người>": người mới thường được gán y hệt người cũ cùng nhóm.
// Chỉ liệt kê người KHÔNG phải admin và đang có trang — admin thấy mọi trang
// nên chép từ họ chẳng nói lên gì.
const nguoiCoTrang = computed(() =>
  agents.value.filter(
    a =>
      a.id !== props.agentId &&
      a.role !== 'administrator' &&
      trangCua(a.id).length
  )
);
const giongNhu = userId => {
  if (!userId) return;
  const ton = new Set(inboxes.value.map(i => i.id));
  chon.value = new Set(trangCua(userId).filter(id => ton.has(id)));
  nguoiMau.value = '';
};

const trangThai = id => {
  const co = chon.value.has(id);
  if (co && !goc.value.has(id)) return 'them';
  if (!co && goc.value.has(id)) return 'bo';
  return '';
};

/**
 * Gọi từ EditAgent sau khi lưu thông tin nhân viên. Trả về câu lỗi, rỗng = ổn.
 * Không đổi gì (hoặc đang là admin, hoặc tải hỏng) thì không gọi API.
 */
const luu = async () => {
  if (props.laAdmin || dangTai.value || loiTai.value) return '';
  if (!thayDoi.value.them && !thayDoi.value.bo) return '';
  try {
    const { data } = await NhanVienAPI.capNhatTrang(props.agentId, [
      ...chon.value,
    ]);
    const moi = data.inbox_ids.map(Number);
    goc.value = new Set(moi);
    chon.value = new Set(moi);
    datTrangCua(props.agentId, moi);
    return '';
  } catch (e) {
    return loiApi(e);
  }
};

defineExpose({ luu });
</script>

<template>
  <div class="w-full pb-4">
    <span class="block pb-1 text-sm font-medium text-n-slate-12">
      {{ $t('BONBOZ.TRANG_NV.TIEU_DE') }}
    </span>

    <p v-if="laAdmin" class="m-0 text-sm text-n-slate-11">
      {{ $t('BONBOZ.TRANG_NV.ADMIN') }}
    </p>
    <p v-else-if="dangTai" class="m-0 text-sm text-n-slate-11">
      {{ $t('BONBOZ.TRANG_NV.DANG_TAI') }}
    </p>
    <p v-else-if="loiTai" class="m-0 text-sm text-n-ruby-11">
      {{ $t('BONBOZ.TRANG_NV.LOI_TAI', { loi: loiTai }) }}
    </p>

    <template v-else>
      <div class="flex flex-wrap items-center gap-2 pb-2 text-sm">
        <button
          type="button"
          class="p-0 text-n-blue-11 hover:underline"
          @click="datHet(true)"
        >
          {{ $t('BONBOZ.TRANG_NV.CHON_HET') }}
        </button>
        <button
          type="button"
          class="p-0 text-n-blue-11 hover:underline"
          @click="datHet(false)"
        >
          {{ $t('BONBOZ.TRANG_NV.BO_HET') }}
        </button>
        <div v-if="nguoiCoTrang.length" class="min-w-40 flex-1">
          <ChonMuc
            :model-value="nguoiMau"
            :muc="nguoiCoTrang"
            icon="i-lucide-copy"
            compact
            :all-label="$t('BONBOZ.TRANG_NV.GIONG_NHU')"
            @update:model-value="giongNhu"
          />
        </div>
      </div>

      <div
        class="flex max-h-60 flex-col gap-0.5 overflow-y-auto rounded-lg border border-n-weak p-1"
        data-testid="trang-duoc-xem"
      >
        <label
          v-for="ib in inboxes"
          :key="ib.id"
          class="!mb-0 flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-n-slate-12 hover:bg-n-alpha-1"
          :class="{
            'bg-n-teal-2': trangThai(ib.id) === 'them',
            'bg-n-ruby-2': trangThai(ib.id) === 'bo',
          }"
        >
          <input
            type="checkbox"
            class="!mb-0 shrink-0"
            :checked="chon.has(ib.id)"
            @change="doi(ib.id)"
          />
          <Avatar
            :src="ib.avatar_url"
            :name="ib.name"
            :size="20"
            rounded-full
          />
          <span class="min-w-0 flex-1 truncate">{{ ib.name }}</span>
          <span
            v-if="trangThai(ib.id)"
            class="shrink-0 text-xs font-medium"
            :class="
              trangThai(ib.id) === 'them' ? 'text-n-teal-11' : 'text-n-ruby-11'
            "
          >
            {{
              trangThai(ib.id) === 'them'
                ? $t('BONBOZ.BO_CHON.MOI_THEM')
                : $t('BONBOZ.BO_CHON.MOI_BO')
            }}
          </span>
        </label>
      </div>

      <p class="m-0 pt-1 text-xs text-n-slate-11">
        {{
          thayDoi.them || thayDoi.bo
            ? $t('BONBOZ.TRANG_NV.SE_LUU', {
                them: thayDoi.them,
                bo: thayDoi.bo,
              })
            : $t('BONBOZ.TRANG_NV.DANG_XEM', {
                n: chon.size,
                tong: inboxes.length,
              })
        }}
      </p>
    </template>
  </div>
</template>
